import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/apiClient';
import AuthLayout from '../components/AuthLayout';

import type { Theme } from '@mui/material/styles';

const getInputSx = (theme: Theme) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '1rem',
    '& fieldset': { borderColor: theme.palette.divider, borderWidth: '2px' },
    '&:hover fieldset': { borderColor: theme.palette.text.secondary },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
    },
    '&.Mui-disabled fieldset': { borderColor: theme.palette.divider, opacity: 0.5 },
  },
  '& .MuiInputLabel-root': { display: 'none' },
  '& .MuiInputBase-input': {
    padding: '0.75rem 1rem',
    '&::placeholder': { color: theme.palette.text.secondary, opacity: 1 },
  },
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: theme.palette.text.disabled,
  },
});

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isValidEmail = (value: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim()) {
      setError(t('forgotPassword.errors.emailRequired', 'Por favor, informe seu email.'));
      return;
    }
    if (!isValidEmail(email)) {
      setError(t('forgotPassword.errors.invalidEmail', 'Por favor, informe um email válido.'));
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await api.forgotPassword(email, {
        redirectUrl: `${window.location.origin}/reset-password`,
      });
      setSuccess(true);
    } catch {
      setSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title={t('forgotPassword.title', 'Esqueceu sua senha?')}
      subtitle={t(
        'forgotPassword.subtitle',
        'Informe seu email e enviaremos instruções para redefinir sua senha.',
      )}
    >
      {!success ? (
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {error && (
              <Alert severity="error" sx={{ borderRadius: '8px' }}>
                {error}
              </Alert>
            )}

            <Stack spacing={0.5}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.9375rem' }}
              >
                {t('forgotPassword.emailLabel', 'Email')}
              </Typography>
              <TextField
                placeholder={t('forgotPassword.emailPlaceholder', 'seu@email.com')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
                disabled={isSubmitting || !email.trim()}
                fullWidth
                inputProps={{
                  'aria-label': t('forgotPassword.emailLabel', 'Email'),
                }}
                sx={getInputSx(theme)}
              />
            </Stack>

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isSubmitting}
              fullWidth
              startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{
                py: 1.5,
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                boxShadow: '0 4px 14px rgba(21, 101, 192, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 6px 20px rgba(21, 101, 192, 0.4)',
                },
                '&:disabled': {
                  background: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                  boxShadow: 'none',
                },
              }}
            >
              {isSubmitting
                ? t('forgotPassword.sending', 'Enviando...')
                : t('forgotPassword.submit', 'Enviar instruções')}
            </Button>

            <Button
              component={Link}
              to="/login"
              variant="text"
              size="small"
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.8125rem',
                color: theme.palette.text.secondary,
              }}
            >
              {t('forgotPassword.backToLogin', 'Voltar ao login')}
            </Button>
          </Stack>
        </Box>
      ) : (
        <Stack spacing={3} alignItems="center" py={2}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(76, 175, 80, 0.3)',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 36, color: '#fff' }} />
          </Box>

          <Typography variant="h6" fontWeight={700} textAlign="center">
            {t('forgotPassword.successTitle', 'Email enviado!')}
          </Typography>

          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t(
              'forgotPassword.successMessage',
              'Se o email estiver cadastrado, você receberá as instruções de recuperação em instantes.',
            )}
          </Typography>

          <Alert severity="info" sx={{ borderRadius: '8px', width: '100%' }}>
            {t(
              'forgotPassword.checkSpam',
              'Verifique também sua pasta de spam ou lixo eletrônico.',
            )}
          </Alert>

          <Button
            component={Link}
            to="/login"
            variant="contained"
            fullWidth
            sx={{
              py: 1.5,
              mt: 1,
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '1rem',
              textTransform: 'none',
              background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
              boxShadow: '0 4px 14px rgba(21, 101, 192, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                boxShadow: '0 6px 20px rgba(21, 101, 192, 0.4)',
              },
            }}
          >
            {t('forgotPassword.backToLogin', 'Voltar ao login')}
          </Button>
        </Stack>
      )}
    </AuthLayout>
  );
}
